import React, { FC } from 'react'

import { Url } from '@/types'
import { Card, type CardProps } from '@components/Card'
import GridList from '@components/GridList'
import { type ImageProps } from '@components/Image'
import { MediaCard } from '@components/MediaCard'
import { Section } from '@components/Section/index'
import Swiper from '@components/Swiper'
import { useTailwindBreakpoint } from '@hooks/useTailwindBreakpoint'

interface CTAProps {
  label: string
  icon: string
  url: Url
}

interface CardSectionProps {
  title: string
  description?: string
  cards: CardProps[]
  cta?: CTAProps
  backgroundColor?: string
  image?: ImageProps
  theme?: string
  variant?: 'default' | 'slider'
}

const CardSection: FC<CardSectionProps> = ({
  title,
  description = '',
  cards = [],
  cta,
  backgroundColor,
  image,
  theme,
  variant = 'default',
}) => {
  const isMobile = useTailwindBreakpoint('lg-')
  let cardsList: React.ReactNode[] = []

  if (variant === 'slider') {
    cardsList = cards.map((card) => (
      <MediaCard
        title={card.title}
        id={card.id}
        image={card.image}
        url={card.url}
        displayFlag={true}
        displayFavorite={card.displayFavorite}
        metadata={card.description}
        metadataClassName="line-clamp-2"
        overlay={{
          position: 'bottom',
          overlayHeight: 'half',
        }}
        className="h-[448px] lg:h-[600px] max-w-[305px] md:max-w-[400px] xl:max-w-[440px]"
        titleClassName="mb-5 leading-7 lg:text-[30px] lg:leading-[35px] lg:text-wrap"
        aspectRatio="aspect-[170/39] lg:aspect-[11/15]"
      />
    ))
  } else {
    cardsList = cards.map((card) => (
      <Card
        key={card.id}
        id={card.id}
        title={card.title}
        image={card.image}
        size={image ? 'sm' : 'lg'}
        aspectRatio={image ? 'aspect-[2/3]' : 'aspect-[7/9]'}
        description={card.description}
        url={card.url}
        displayFlag={true}
        displayFavorite={card.displayFavorite}
        overlay={{
          position: image ? 'bottom' : 'top',
          className: image
            ? 'lg:text-[22px] lg:leading-[26px]'
            : 'lg:text-[34px] lg:leading-[38px]',
        }}
      />
    ))
  }

  const CardsGroup = () => {
    return cardsList
  }

  const SwiperOptions = {
    gap: '16px',
    fixedWidth: '100%',
  }

  return (
    <Section
      backgroundColor={backgroundColor}
      image={image}
      theme={theme as 'light' | 'dark'}
      stickyOnBorder={image && variant === 'slider'}
    >
      <Section.Content
        offset={image ? true : false}
        stickOnBorder={image && variant === 'slider'}
        theme={theme as 'light' | 'dark'}
        className={`${image ? (variant === 'slider' ? 'ltr:pr-0 flex w-full self-end lg:self-auto lg:w-auto md:ml-spacing-lg' : 'ltr:pr-0 flex w-full self-end lg:self-auto lg:w-auto md:mx-spacing-lg') : ''}`}
      >
        <Section.Title
          title={title}
          className={`${variant === 'slider' ? 'ltr:pr-[35px] rtl:pl-[35px]' : ''}`}
        />
        <Section.Description
          description={description}
          className={`text-sm mt-4 lg:mt-0 ${variant === 'slider' ? 'ltr:pr-[35px] rtl:pl-[35px]' : ''}`}
        />
        {variant === 'slider' ? (
          <div className="mt-8">
            <Swiper
              slides={cardsList}
              options={{
                type: 'carousel',
                autoWidth: true,
                gap: 0,
              }}
              paginationClassnames="ltr:lg:ml-0 rtl:lg:mr-0 ltr:mr-7 rtl:ml-7"
            />
          </div>
        ) : isMobile && image ? (
          <div className="mt-8">
            <Swiper
              slides={cardsList}
              options={SwiperOptions}
              className="[&_ul]:items-start"
              paginationClassnames="ltr:mr-7 rtl:ml-7"
            />
          </div>
        ) : (
          <GridList className="gap-y-8 gap-x-[2%] mt-8 xl:grid-cols-3">
            <CardsGroup />
          </GridList>
        )}
        {cta?.url && (
          <Section.CTA
            className={`${variant === 'slider' ? 'self-start' : ''}`}
            {...cta}
          >
            {cta.label}
          </Section.CTA>
        )}
      </Section.Content>
    </Section>
  )
}

export default CardSection
